import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  // Axios 인터셉터 설정
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  // 초기 인증 상태 확인
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        if (response.data.success) {
          setUser(response.data.data.user);
        } else {
          handleLogout();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        handleLogout();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [token]);

  const handleLogin = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });

      if (response.data.success) {
        const { user: userData, token: newToken } = response.data.data;
        setUser(userData);
        setToken(newToken);
        localStorage.setItem('token', newToken);
        return { success: true };
      }

      return { success: false, error: response.data.error };
    } catch (error) {
      const message = error.response?.data?.error || '로그인에 실패했습니다.';
      return { success: false, error: message };
    }
  }, []);

  const handleRegister = useCallback(async (email, password, name) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        email,
        password,
        firstName: name,
        lastName: '',
      });

      if (response.data.success) {
        const { user: userData, token: newToken } = response.data.data;
        setUser(userData);
        setToken(newToken);
        localStorage.setItem('token', newToken);
        return { success: true };
      }

      return { success: false, error: response.data.error };
    } catch (error) {
      const message = error.response?.data?.error || '회원가입에 실패했습니다.';
      return { success: false, error: message };
    }
  }, []);

  const handleLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      if (response.data.success) {
        setUser(response.data.data.user);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, [token]);

  const isAdmin = !!(user?.isAdmin || user?.role === 'admin' || user?.accessLevel === 'Level5');
  const plan = user?.plan === 'paid' || user?.plan === 'free'
    ? user.plan
    : (isAdmin || (user?.accessLevel && user.accessLevel !== 'Level1') ? 'paid' : 'free');

  const value = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    accessLevel: user?.accessLevel || (isAdmin ? 'Level5' : plan === 'paid' ? 'Level2' : 'Level1'),
    plan,
    isAdmin,
    isPaid: isAdmin || plan === 'paid',
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
